import { useState, useEffect } from "react"
import { useAddNewUserMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faUserPlus } from "@fortawesome/free-solid-svg-icons"
import { ROLES } from "../../config/roles"
import useTitle from "../../hooks/useTitle"
import { toast } from 'react-toastify'


const USER_REGEX = /^[A-z]{3,20}$/
const PWD_REGEX = /^[A-z0-9!@#$%]{4,15}$/

const NewUserForm = () => {
    useTitle('DPE: New User')

    const [addNewUser, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewUserMutation()

    const navigate = useNavigate()

    const [username, setUsername] = useState('')
    const [firstname, setFirstname] = useState('')
    const [lastname, setLastname] = useState('')
    const [validUsername, setValidUsername] = useState(false)
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(false)
    const [roles, setRoles] = useState(["Physical Therapist"])
    const [clinic, setClinic] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')

    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(password))
    }, [password])

    useEffect(() => {
        if (isSuccess) {
            setUsername('')
            setFirstname('')
            setLastname('')
            setPassword('')
            setRoles([])
            setClinic('')
            setEmail('')
            setPhone('')
            toast.success("User was Created");
            navigate('/dash/users')
        }

        if (isError) {
            toast.error(error?.data?.message);
        }

    }, [isSuccess, navigate, isError, error])

    const onUsernameChanged = e => setUsername(e.target.value)
    const onFirstnameChanged = e => setFirstname(e.target.value)
    const onLastnameChanged = e => setLastname(e.target.value)
    const onPasswordChanged = e => setPassword(e.target.value)
    const onClinicChanged = e => setClinic(e.target.value)
    const onEmailChanged = e => setEmail(e.target.value)
    const onPhoneChanged = e => setPhone(e.target.value)


    const onRolesChanged = e => {
        const values = Array.from(
            e.target.selectedOptions, //HTMLCollection 
            (option) => option.value
        )
        setRoles(values)
    }

    const canSave = [roles.length, validUsername, validPassword].every(Boolean) && !isLoading

    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            await addNewUser({ username, firstname, lastname, password, roles, clinic, email, phone })
        } else {
            if (!validUsername) {
                toast.error("Username is not valid")
            } else if (!validPassword) {
                toast.error("Password is not valid")
            } else if (!roles.length) {
                toast.error("Select role(s)")
            } else {
                toast.error('Username, Firstname, and Lastname are required');
            }
        }
    }

    const options = Object.values(ROLES).map(role => {
        return (
            <option key={role} value={role}>
                {role}
            </option >
        )
    })

    const content = (
        <section id="main_content">
            <header>
                <h1 className="text-steel mt-5"><FontAwesomeIcon icon={faUserPlus} style={{fontSize:"30px", marginRight:"5px"}} />New User</h1>
            </header>
            <main>
                <form onSubmit={e => e.preventDefault()}>
                <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="username">
                                Username: <span className="text-danger">*</span><br/>
                                <span>[3-20 letters]</span>
                            </label>
                            <input
                                className="form-control"
                                id="username"
                                name="username"
                                type="text"
                                autoComplete="off"
                                value={username}
                                onChange={onUsernameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="clinic">
                                Clinic<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="clinic"
                                name="clinic"
                                type="text"
                                autoComplete="off"
                                value={clinic}
                                onChange={onClinicChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="password">
                                Password: <span className="text-danger">*</span><br/>
                                <span>[empty = no change]</span><span>[4-15 chars incl. !@#$%]</span>
                            </label>
                            <input
                                className="form-control"
                                id="password"
                                name="password"
                                type="password"
                                value={password}
                                onChange={onPasswordChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="email">
                                Email<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="off"
                                value={email}
                                onChange={onEmailChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="firstname">
                                Firstname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="firstname"
                                name="firstname"
                                type="text"
                                autoComplete="off"
                                value={firstname}
                                onChange={onFirstnameChanged}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label" htmlFor="lastname">
                                Lastname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="lastname"
                                name="lastname"
                                type="text"
                                autoComplete="off"
                                value={lastname}
                                onChange={onLastnameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="phone">
                                Phone<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="phone"
                                name="phone"
                                type="text"
                                autoComplete="off"
                                value={phone}
                                onChange={onPhoneChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-4 text-end">
                            <label className="form-label" htmlFor="roles">
                                ASSIGNED ROLES:
                            </label>
                        </div>
                        <div className="col-md-3">
                            <select
                                id="roles"
                                name="roles"
                                className="form-select"
                                multiple={true}
                                size="3"
                                value={roles}
                                onChange={onRolesChanged}
                            >
                                {options}
                            </select>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12 text-end">
                            <br/>
                            <span
                                role="button"
                                className="text-success p-2 icon"
                                title="Save"
                                onClick={onSaveUserClicked}
                            >
                                Create User
                                <FontAwesomeIcon icon={faSave}  className="fa-icon"/>
                            </span>
                        </div>
                    </div>
                </form>
            </main>
        </section>    )

    return content
}
export default NewUserForm